import Ajv from 'ajv';
import addFormats from 'ajv-formats';
import type { UriIssueReason } from '../../types';
import { URI_MESSAGES } from '../../constants';

// RFC 3986 scheme: ALPHA *( ALPHA / DIGIT / "+" / "-" / "." ) followed by a colon.
const SCHEME_REGEX = /^[a-zA-Z][a-zA-Z0-9+.-]*:/;

/**
 * Determines if an href uses a blocked (i.e., non‑allowed) explicit protocol.
 *
 * Returns false when:
 *  - The href is relative / fragment / query only (no explicit scheme)
 *  - The scheme matches an allowed protocol
 * Returns true when the href has an explicit scheme not present in the allow list.
 */
export const isBlockedProtocol = (
  href: string,
  allowed: string[] = [],
): boolean => {
  const match = href.trim().toLowerCase().match(SCHEME_REGEX);
  if (!match) {
    // No explicit scheme (relative / fragment / query) => cannot be blocked.
    return false;
  }
  const proto = match[0].slice(0, -1); // Remove trailing ':'
  return !allowed.includes(proto);
};

// Extract protocol scheme (without trailing colon) from an href if present
export const extractProtocol = (href?: string): string | undefined => {
  if (!href) return undefined;
  const m = href.trim().match(/^([a-zA-Z][a-zA-Z0-9+.-]*):/);
  return m ? m[1].toLowerCase() : undefined;
};

/**
 * Basic structural validation used by higher-level keywords (rich text + enforceUriProtocols) to decide
 * if a link is malformed vs merely policy‑blocked.
 *
 * Current (Ajv-based) rules:
 *
 *  - Fails Ajv RFC 3986 `uri-reference` format => invalid
 *    (Ajv accepts relative paths, fragments, and query‑only references per the spec)
 *  - If the parsed protocol is explicitly blocked => invalid structurally for our purposes (we treat
 *    blocked protocols as failing earlier stage rather than returning true here)
 *
 * Note: We purposely pass the allow list so we can short-circuit blocked protocols here when structural
 * validity is being used as a guard prior to distinguishing reason codes.
 */
// Ajv instance for URI reference validation (RFC 3986). Created once.
const ajv = new Ajv({ allErrors: false, strict: false });
addFormats(ajv); // registers 'uri-reference'
const validateUriRef = ajv.compile<{ type: 'string' }>({
  type: 'string',
  format: 'uri-reference',
});

export const isHrefStructurallyValid = (
  href: string,
  allowed: string[] = [],
): boolean => {
  if (!href) return false;
  // Use Ajv to validate RFC 3986 URI-reference syntax (accepts relative, fragment, query forms)
  if (!validateUriRef(href)) return false;
  // If there is an explicit scheme, enforce allow list
  const protocol = extractProtocol(href);
  if (protocol && !allowed.includes(protocol)) return false;
  // Ajv's uri-reference format allows only a scheme like https:// this (it is technically a valid scheme + empty path)
  if (protocol) {
    // Detect patterns like "scheme://" or "scheme:///" with nothing after the slashes except optional fragment/query
    // We require at least one non-slash, non-fragment, non-query character following the authority-introducing '//'
    const afterScheme = href.slice(protocol.length + 1); // portion after 'scheme:'
    if (afterScheme.startsWith('//')) {
      const rest = afterScheme.slice(2); // after '//'
      // Strip leading slashes (redundant), then check if first meaningful char starts a fragment or query or is empty
      const trimmed = rest.replace(/^\/+/g, '');
      if (trimmed === '' || /^[#?]/.test(trimmed)) return false;
    }
  }
  return true;
};

export const getUriMessage = (
  reason: UriIssueReason,
  href?: string,
): string => {
  if (reason === 'blockedProtocol') {
    const proto = extractProtocol(href);
    if (proto) return `${URI_MESSAGES[reason]} (${proto})`;
  }
  return URI_MESSAGES[reason];
};
